import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef } from 'react';
import { getProfil } from '../api';
import { anschreibenAbsenderZeilen, buildAnschreibenAbsender } from '../utils/anschreibenAbsender';

const AN_BACKDROP_PADDING = 16;

function useContainerScale(containerRef) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const available = el.offsetWidth - AN_BACKDROP_PADDING * 2;
      setScale(Math.min(available / 794, 1));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);
  return scale;
}

const UndoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.4" d="M7.81 2H16.18C19.83 2 22 4.17 22 7.81V16.18C22 19.82 19.83 21.99 16.19 21.99H7.81C4.17 22 2 19.83 2 16.19V7.81C2 4.17 4.17 2 7.81 2Z"/>
    <path d="M13.92 8.97957H8.77L9.1 8.64957C9.39 8.35957 9.39 7.87957 9.1 7.58957C8.81 7.29957 8.33 7.29957 8.04 7.58957L6.47 9.15957C6.18 9.44957 6.18 9.92957 6.47 10.2196L8.04 11.7896C8.19 11.9396 8.38 12.0096 8.57 12.0096C8.76 12.0096 8.95 11.9396 9.1 11.7896C9.39 11.4996 9.39 11.0196 9.1 10.7296L8.84 10.4696H13.92C15.2 10.4696 16.25 11.5096 16.25 12.7996C16.25 14.0896 15.21 15.1296 13.92 15.1296H9C8.59 15.1296 8.25 15.4696 8.25 15.8796C8.25 16.2896 8.59 16.6296 9 16.6296H13.92C16.03 16.6296 17.75 14.9096 17.75 12.7996C17.75 10.6896 16.03 8.97957 13.92 8.97957Z"/>
  </svg>
);

const RedoIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.4" d="M7.81 2H16.18C19.83 2 22 4.17 22 7.81V16.18C22 19.82 19.83 21.99 16.19 21.99H7.81C4.17 22 2 19.83 2 16.19V7.81C2 4.17 4.17 2 7.81 2Z"/>
    <path d="M17.53 9.15957L15.96 7.58957C15.67 7.29957 15.19 7.29957 14.9 7.58957C14.61 7.87957 14.61 8.35957 14.9 8.64957L15.23 8.97957H10.08C7.97 8.97957 6.25 10.6996 6.25 12.8096C6.25 14.9196 7.97 16.6396 10.08 16.6396H15C15.41 16.6396 15.75 16.2996 15.75 15.8896C15.75 15.4796 15.41 15.1396 15 15.1396H10.08C8.8 15.1396 7.75 14.0996 7.75 12.8096C7.75 11.5196 8.79 10.4796 10.08 10.4796H15.16L14.9 10.7396C14.61 11.0296 14.61 11.5096 14.9 11.7996C15.05 11.9496 15.24 12.0196 15.43 12.0196C15.62 12.0196 15.81 11.9496 15.96 11.7996L17.53 10.2296C17.82 9.92957 17.82 9.45957 17.53 9.15957Z"/>
  </svg>
);

const EditPencilIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.26 3.60014L5.04997 12.2901C4.73997 12.6201 4.43997 13.2701 4.37997 13.7201L4.00997 16.9601C3.87997 18.1301 4.71997 18.9301 5.87997 18.7301L9.09997 18.1801C9.54997 18.1001 10.18 17.7701 10.49 17.4301L18.7 8.74014C20.12 7.24014 20.76 5.53014 18.55 3.44014C16.35 1.37014 14.68 2.10014 13.26 3.60014Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.89 5.05005C12.32 7.81005 14.56 9.92005 17.34 10.2" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 22H21" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Parser ────────────────────────────────────────────────────

function parseAnschreiben(rohtext) {
  const zeilen = rohtext
    .split('\n')
    .map((z) => z.trim())
    .filter((z) => z && !z.startsWith('---'));

  let betreff = '';
  let betreffRohzeile = '';
  let anrede = '';
  const paragraphen = [];
  let grussGefunden = false;

  for (const zeile of zeilen) {

    const zeileOhneSterne = zeile.replace(/\*\*/g, '').trim();
    if (!betreff && /^bewerbung\s+als/i.test(zeileOhneSterne)) {
      betreff = zeileOhneSterne;
      betreffRohzeile = zeile;
      continue;
    }

    if (/^sehr geehrte/i.test(zeileOhneSterne)) {
      anrede = zeileOhneSterne;
      continue;
    }

    if (zeile.startsWith('Mit freundlichen')) {
      grussGefunden = true;
      continue;
    }

    if (grussGefunden) continue;

    paragraphen.push({ text: zeile.replace(/\*\*/g, '').trim(), rohzeile: zeile });
  }

  const anredeAnzeige = anrede || 'Sehr geehrte Damen und Herren,';
  return { betreff, betreffRohzeile, anrede: anredeAnzeige, paragraphen };
}

// ── Anschreiben Template ──────────────────────────────────────

function AnschreibenLayout({ rohtext, nutzer, stelle, template, onEditToggle, onAbsenderToggle, onEmpfaengerToggle, showTapHint }) {
  const { betreff, betreffRohzeile, anrede, paragraphen } = parseAnschreiben(rohtext);

  const datum = stelle?.generiert_am
    ? new Date(stelle.generiert_am).toLocaleDateString('de-DE', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : new Date().toLocaleDateString('de-DE', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

  const absenderDaten = buildAnschreibenAbsender(stelle, nutzer);
  const vollName = absenderDaten.name || `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const absenderTeile = anschreibenAbsenderZeilen(stelle, nutzer);

  return (
    <div className={`anschreiben an-${template}`}>
      <div className={`an-absender-wrapper${onAbsenderToggle ? ' an-absender-editierbar' : ''}`}>
        <div className="an-absender">
          {absenderTeile.map((teil, i) => <div key={i}>{teil}</div>)}
        </div>
        {onAbsenderToggle && (
          <button
            className="an-edit-btn an-absender-edit-btn"
            onClick={onAbsenderToggle}
            title="Absender bearbeiten"
          >
            <EditPencilIcon size={13} />
          </button>
        )}
      </div>

      <div className={`an-empfaenger-wrapper${onEmpfaengerToggle ? ' an-empfaenger-editierbar' : ''}`}>
        <div className="an-empfaenger">
          <div className="an-empfaenger-firma">{stelle?.unternehmen || stelle?.name || ''}</div>
          {stelle?.empfaenger_ansprechperson && <div>{stelle.empfaenger_ansprechperson}</div>}
          {stelle?.empfaenger_strasse && <div>{stelle.empfaenger_strasse}</div>}
          {stelle?.empfaenger_plz_ort && <div>{stelle.empfaenger_plz_ort}</div>}
          {stelle?.job_id && <div>Job-ID: {stelle.job_id}</div>}
        </div>
        {onEmpfaengerToggle && (
          <button
            className="an-edit-btn an-empfaenger-edit-btn"
            onClick={onEmpfaengerToggle}
            title="Empfänger bearbeiten"
          >
            <EditPencilIcon size={13} />
          </button>
        )}
      </div>

      <div className="an-datum">{nutzer?.ort || nutzer?.plz_ort?.split(' ').slice(1).join(' ') || ''}, {datum}</div>

      {betreff && (
        <div className="an-absatz-editierbar">
          <div className={`tap-zone-overlay${showTapHint ? ' is-pulsing' : ''}`} />
          <div className="an-betreff">{betreff}</div>
          {onEditToggle && (
            <button
              className="an-edit-btn"
              onClick={() => onEditToggle({ text: betreff, rohzeile: betreffRohzeile, label: 'Betreff' })}
              title="Bearbeiten"
            >
              <EditPencilIcon size={13} />
            </button>
          )}
        </div>
      )}

      <div className="an-absatz-editierbar">
        <div className={`tap-zone-overlay${showTapHint ? ' is-pulsing' : ''}`} />
        <div className="an-anrede">{anrede}</div>
        {onEditToggle && (
          <button
            className="an-edit-btn"
            onClick={() => onEditToggle({ text: anrede, rohzeile: anrede, label: 'Anrede' })}
            title="Anrede bearbeiten"
          >
            <EditPencilIcon size={13} />
          </button>
        )}
      </div>

      {paragraphen.map((p, i) => (
        <div key={i} className="an-absatz-editierbar">
          <div className={`tap-zone-overlay${showTapHint ? ' is-pulsing' : ''}`} />
          <div className="an-paragraph">{p.text}</div>
          {onEditToggle && (
            <button
              className="an-edit-btn"
              onClick={() => onEditToggle({ text: p.text, rohzeile: p.rohzeile, label: `Absatz ${i + 1}` })}
              title="Bearbeiten"
            >
              <EditPencilIcon size={13} />
            </button>
          )}
        </div>
      ))}

      <div className="an-gruss">Mit freundlichen Grüßen</div>
      <div className="an-unterschrift">{vollName}</div>
    </div>
  );
}

// ── Haupt-Component ───────────────────────────────────────────

const MobileAnschreibenVorschau = forwardRef(function MobileAnschreibenVorschau(
  { stelle, onEditToggle, onAbsenderToggle, onEmpfaengerToggle, onUndo, onRedo, canUndo, canRedo }, ref
) {
  const [profil, setProfil] = useState(null);
  const [showTapHint, setShowTapHint] = useState(
    () => !sessionStorage.getItem('cv_tap_hint_shown')
  );
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(1123);
  const scale = useContainerScale(outerRef);

  useEffect(() => {
    if (!showTapHint) return;
    const t = setTimeout(() => {
      sessionStorage.setItem('cv_tap_hint_shown', '1');
      setShowTapHint(false);
    }, 1600);
    return () => clearTimeout(t);
  }, [showTapHint]);

  useEffect(() => {
    if (!stelle?.profil_name) return;
    getProfil(stelle.profil_name)
      .then((r) => setProfil(r.data))
      .catch(() => {});
  }, [stelle?.profil_name]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const update = () => setContentHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stelle?.an_rohtext, scale]);

  if (!stelle?.an_rohtext) return null;

  const istModern = stelle.cv_data
    ? (stelle.cv_data.sektionen || []).some((s) => s.typ === 'kenntnisse')
    : stelle.cv_rohtext
      ? (stelle.cv_rohtext.includes('KTITEL:') ||
         (stelle.cv_rohtext.includes('FIRMA:') && !stelle.cv_rohtext.includes('JOBTITEL:')))
      : true;
  const template = istModern ? 'modern' : 'modern';

  const handleEditToggle = onEditToggle
    ? (item) => onEditToggle(item, profil, stelle.an_rohtext)
    : undefined;

  const wrapperHeight = Math.round(contentHeight * scale + AN_BACKDROP_PADDING * 2);

  return (
    <div className="an-vorschau-wrapper">
      <div className="an-vorschau-label">
        <span>Anschreiben Vorschau</span>
        {(onUndo || onRedo) && (
          <div className="cv-history-btns">
            <button className="cv-history-btn" onClick={onUndo} disabled={!canUndo} title="Rückgängig">
              <UndoIcon />
            </button>
            <button className="cv-history-btn" onClick={onRedo} disabled={!canRedo} title="Wiederherstellen">
              <RedoIcon />
            </button>
          </div>
        )}
      </div>

      {/* Skalierter Viewport-Container */}
      <div
        ref={outerRef}
        style={{
          width: '100%',
          overflow: 'hidden',
          height: wrapperHeight,
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-island)',
          boxSizing: 'border-box',
          padding: `${AN_BACKDROP_PADDING}px`,
        }}
      >
        <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: 794 }}>
          <div
            className="an-paginas-container an-paginas-mobile"
            ref={(el) => {
              innerRef.current = el;
              if (typeof ref === 'function') ref(el);
              else if (ref) ref.current = el;
            }}
          >
            <div className={`an-pagina an-pagina-${template}`}>
              <AnschreibenLayout
                rohtext={stelle.an_rohtext}
                nutzer={profil}
                stelle={stelle}
                template={template}
                onEditToggle={handleEditToggle}
                onAbsenderToggle={onAbsenderToggle}
                onEmpfaengerToggle={onEmpfaengerToggle}
                showTapHint={showTapHint}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default MobileAnschreibenVorschau;
